import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class LoggingApplicationInsightsService {
  private readonly isBrowser: boolean;
  private appInsights: ApplicationInsights | null = null;

  constructor(
    private authService: AuthService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (!this.isBrowser) {
      return;
    }

    const instrumentationKey = this.getInstrumentationKey();
    if (!instrumentationKey) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey,
        enableAutoRouteTracking: false
      }
    });
    this.appInsights.loadAppInsights();
  }

  private getInstrumentationKey() {
    try {
      return environment.appInsights.instrumentationKey;
    }
    catch (e) {
      return "";
    }
  }

  logPageView(name?: string, url?: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    try {
      this.appInsights.trackPageView({
        name: name,
        uri: url,
        properties: this.getDefaultProperties(properties)
      });
    } catch (e) {
      console.error('Error logging page view to Application Insights', e);
    }
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    try {
      this.appInsights.trackEvent({ name: name }, this.getDefaultProperties(properties));
    } catch (e) {
    }
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    try {
      this.appInsights.trackMetric({ name: name, average: average }, this.getDefaultProperties(properties));
    } catch (e) {
    }
  }

  logException(exception: Error, severityLevel?: number) {
    if (!this.appInsights) {
      return;
    }

    try {
      this.appInsights.trackException({ exception: exception, severityLevel: severityLevel }, this.getDefaultProperties());
    } catch (e) {
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    try {
      this.appInsights.trackTrace({ message: message }, this.getDefaultProperties(properties));
    } catch (e) {
    }
  }

  private getDefaultProperties(properties?: { [key: string]: any }) {
    return {
      ...properties,
      application: 'FoMaClient',
      environment: this.getEnvironment(),
      clientVersion: environment.version,
      user: this.getUser()
    };
  }

  private getUser() {
    return this.authService.userDetails()?.email || "unknown";
  }

  private getEnvironment() {
    if (environment.environmentName === '') {
      return "Prod";
    }
    const envLowerCase = environment.environmentName.toLowerCase();

    return envLowerCase.charAt(0).toUpperCase() + envLowerCase.slice(1);
  }
}
